// LP Trade Lifecycle State Machine
// Pattern follows src/lib/kyc/stateMachine.js — Object.freeze with role-gated transitions
//
// Lifecycle:
// booked -> confirmed -> instructed -> awaiting_funds -> funds_received -> released -> settled -> csd_payment_confirmed
//
// Role gates:
//   - released requires lp_operations only (dealers cannot release)
// Pre-fund gate:
//   - released blocked until funds_received when preFund=true
// Late-funds alert:
//   - fires when awaiting_funds past settlement.cutoffLocal

import { LP_TRADE_STATUSES, LP_ROLES } from './constants';

// ─── State Transition Map ───────────────────────────────────────────────
const TRANSITIONS = Object.freeze({
  [LP_TRADE_STATUSES.BOOKED]: [
    LP_TRADE_STATUSES.CONFIRMED,
    LP_TRADE_STATUSES.CANCELLED,
  ],

  [LP_TRADE_STATUSES.CONFIRMED]: [
    LP_TRADE_STATUSES.INSTRUCTED,
    LP_TRADE_STATUSES.CANCELLED,
  ],

  [LP_TRADE_STATUSES.INSTRUCTED]: [
    LP_TRADE_STATUSES.AWAITING_FUNDS,
    LP_TRADE_STATUSES.CANCELLED,
  ],

  [LP_TRADE_STATUSES.AWAITING_FUNDS]: [
    LP_TRADE_STATUSES.FUNDS_RECEIVED,
    LP_TRADE_STATUSES.FAILED,
  ],

  [LP_TRADE_STATUSES.FUNDS_RECEIVED]: [
    { state: LP_TRADE_STATUSES.RELEASED, roles: [LP_ROLES.LP_OPERATIONS] },
    LP_TRADE_STATUSES.FAILED,
  ],

  [LP_TRADE_STATUSES.RELEASED]: [
    LP_TRADE_STATUSES.SETTLED,
    LP_TRADE_STATUSES.FAILED,
  ],

  [LP_TRADE_STATUSES.SETTLED]: [
    LP_TRADE_STATUSES.CSD_PAYMENT_CONFIRMED,
  ],

  // Terminal states
  [LP_TRADE_STATUSES.CSD_PAYMENT_CONFIRMED]: [],
  [LP_TRADE_STATUSES.CANCELLED]: [],
  [LP_TRADE_STATUSES.FAILED]: [],
});

// ─── Terminal States ────────────────────────────────────────────────────
const TERMINAL_STATES = Object.freeze(new Set([
  LP_TRADE_STATUSES.CSD_PAYMENT_CONFIRMED,
  LP_TRADE_STATUSES.CANCELLED,
  LP_TRADE_STATUSES.FAILED,
]));

// ─── Settlement Pipeline Steps (ordered) ────────────────────────────────
const PIPELINE_ORDER = Object.freeze([
  LP_TRADE_STATUSES.BOOKED,
  LP_TRADE_STATUSES.CONFIRMED,
  LP_TRADE_STATUSES.INSTRUCTED,
  LP_TRADE_STATUSES.AWAITING_FUNDS,
  LP_TRADE_STATUSES.FUNDS_RECEIVED,
  LP_TRADE_STATUSES.RELEASED,
  LP_TRADE_STATUSES.SETTLED,
  LP_TRADE_STATUSES.CSD_PAYMENT_CONFIRMED,
]);

// ─── Helpers ────────────────────────────────────────────────────────────

const normalise = (entry) =>
  typeof entry === 'string' ? { state: entry, roles: null } : entry;

/**
 * Check whether a transition from currentState to targetState is defined
 * (ignores role and business rules).
 */
export function canTransition(currentState, targetState) {
  const allowed = TRANSITIONS[currentState];
  if (!allowed) return false;
  return allowed.some((t) => normalise(t).state === targetState);
}

/**
 * Return reachable states from currentState, optionally filtered by role.
 */
export function getAvailableTransitions(currentState, userRole) {
  const allowed = TRANSITIONS[currentState];
  if (!allowed) return [];

  return allowed
    .map(normalise)
    .filter(({ roles }) => {
      if (!roles) return true;
      if (!userRole) return true;
      return roles.includes(userRole);
    })
    .map(({ state }) => state);
}

/**
 * Validate transition considering role and pre-fund gate.
 * @param {string} currentState
 * @param {string} targetState
 * @param {string} userRole
 * @param {object} tradeContext - { preFund: boolean }
 * @returns {{ valid: boolean, reason?: string }}
 */
export function validateTransition(currentState, targetState, userRole, tradeContext = {}) {
  if (TERMINAL_STATES.has(currentState)) {
    return { valid: false, reason: `Trade is in terminal state "${currentState}" and cannot transition.` };
  }

  const allowed = TRANSITIONS[currentState];
  if (!allowed) {
    return { valid: false, reason: `Unknown trade state "${currentState}".` };
  }

  const match = allowed.find((t) => normalise(t).state === targetState);
  if (!match) {
    return { valid: false, reason: `Transition from "${currentState}" to "${targetState}" is not defined.` };
  }

  const { roles } = normalise(match);
  if (roles && userRole && !roles.includes(userRole)) {
    return {
      valid: false,
      reason: `Role "${userRole}" cannot transition to "${targetState}". Required: ${roles.join(', ')}.`,
    };
  }

  // Pre-fund gate: cannot release unless funds have been received
  if (targetState === LP_TRADE_STATUSES.RELEASED && tradeContext.preFund) {
    if (currentState !== LP_TRADE_STATUSES.FUNDS_RECEIVED) {
      return {
        valid: false,
        reason: 'Pre-fund gate: funds must be received before release.',
      };
    }
  }

  return { valid: true };
}

/**
 * Check whether a trade is in a terminal state.
 */
export function isTerminal(state) {
  return TERMINAL_STATES.has(state);
}

/**
 * Get the pipeline step index for a given state (for stepper UI).
 * Returns -1 if not found or if terminal failure/cancellation.
 */
export function getPipelineIndex(state) {
  const idx = PIPELINE_ORDER.indexOf(state);
  return idx;
}

/**
 * Get the ordered pipeline steps.
 */
export function getPipelineSteps() {
  return [...PIPELINE_ORDER];
}

/**
 * Check if the trade is past the expected cutoff (for late-funds alerts).
 */
export function isLateFunds(trade) {
  if (trade.status !== LP_TRADE_STATUSES.AWAITING_FUNDS) return false;
  if (!trade.settlement?.cutoffLocal) return false;
  return new Date() > new Date(trade.settlement.cutoffLocal);
}

export default {
  canTransition,
  getAvailableTransitions,
  validateTransition,
  isTerminal,
  getPipelineIndex,
  getPipelineSteps,
  isLateFunds,
  TRANSITIONS,
  TERMINAL_STATES,
  PIPELINE_ORDER,
};
